// Languages API client (MOD-09, Blok 2) — content i18n dil kataloğu.
//
// Uçlar `/api/v1/admin/languages/...` altında ve `ActionResponse<T>` döndürür.
// İş kuralları BE'de zorlanır (tek default; default silinemez/pasifleşemez;
// kullanımdaki dil silinemez → LANGUAGE_DEFAULT_IMMUTABLE / LANGUAGE_IN_USE).

import { http, unwrap, unwrapCreatedId, unwrapList, type ActionResponse } from './http';
import type {
  CreateLanguageRequest,
  Language,
  LanguageCreateInput,
  LanguageDtoWire,
  LanguageUpdateInput,
  UpdateLanguageRequest,
} from '@features/languages/types';

/** Generated DTO'nun tümü optional; UI için garanti alanlı FE tipine daralt. */
function mapLanguage(d: LanguageDtoWire): Language {
  return {
    id: d.id ?? '',
    code: d.code ?? '',
    name: d.name ?? '',
    isDefault: d.isDefault ?? false,
    isActive: d.isActive ?? false,
    displayOrder: d.displayOrder ?? 0,
    updatedAt: d.updatedAt ?? '',
    rowVersion: d.rowVersion ?? null,
  };
}

export const languagesApi = {
  /** Tüm diller (varsayılan: hepsi). `activeOnly` ile yalnızca aktif olanlar. */
  async list(activeOnly = false): Promise<Language[]> {
    const qs = activeOnly ? '?activeOnly=true' : '';
    const res = await http.get<ActionResponse<LanguageDtoWire>>(
      `/api/v1/admin/languages${qs}`,
    );
    const { items } = unwrapList(res);
    return items
      .map(mapLanguage)
      .sort((a, b) => a.displayOrder - b.displayOrder || a.code.localeCompare(b.code));
  },

  async get(id: string): Promise<Language> {
    const res = await http.get<ActionResponse<LanguageDtoWire>>(
      `/api/v1/admin/languages/${id}`,
    );
    return mapLanguage(unwrap(res));
  },

  /** POST → GuidActionResponse (yeni id); tam kaydı geri çekip döndürür. */
  async create(input: LanguageCreateInput): Promise<Language> {
    const body: CreateLanguageRequest = {
      code: input.code,
      name: input.name,
      isDefault: input.isDefault,
      isActive: input.isActive,
      displayOrder: input.displayOrder,
    };
    const res = await http.post<ActionResponse<string | { id: string }>>(
      '/api/v1/admin/languages',
      body,
    );
    return languagesApi.get(unwrapCreatedId(res));
  },

  /** PUT → BooleanActionResponse; güncel kaydı geri çekip döndürür. */
  async update(id: string, input: LanguageUpdateInput): Promise<Language> {
    const body: UpdateLanguageRequest = {
      name: input.name,
      isDefault: input.isDefault,
      isActive: input.isActive,
      displayOrder: input.displayOrder,
      rowVersion: input.rowVersion,
    };
    const res = await http.put<ActionResponse<boolean>>(
      `/api/v1/admin/languages/${id}`,
      body,
    );
    unwrap(res);
    return languagesApi.get(id);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(
      `/api/v1/admin/languages/${id}`,
    );
    unwrap(res);
  },
};
